import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../components/AuthProvider';
import {
  SNIPPET_BACKLINKS_CHANGED,
  backlinksMapForDoc,
  createEmptyBacklinks,
  fetchSnippetBacklinksIndex,
  getBacklinksForBlock,
} from '../utils/snippetBacklinks';

/**
 * Live snippet reverse-index for the current workspace.
 * Pass `docId` to get a ready-made map of blockId → consumer entries
 * for origin badges in that doc.
 */
export function useSnippetBacklinks(docId = null) {
  const { workspaceId, loading: authLoading } = useAuth();
  const [index, setIndex] = useState(createEmptyBacklinks);
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await fetchSnippetBacklinksIndex();
      setIndex(next);
      setReady(true);
    } catch (e) {
      console.warn('Snippet backlinks failed to load:', e);
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    setReady(false);
    setIndex(createEmptyBacklinks());
    load();
  }, [workspaceId, authLoading, load]);

  useEffect(() => {
    const onChanged = (e) => {
      if (e.detail?.index) setIndex(e.detail.index);
      else load();
    };
    window.addEventListener(SNIPPET_BACKLINKS_CHANGED, onChanged);
    return () => window.removeEventListener(SNIPPET_BACKLINKS_CHANGED, onChanged);
  }, [load]);

  const backlinksByBlockId = useMemo(
    () => (docId ? backlinksMapForDoc(index, docId) : {}),
    [index, docId]
  );

  const getBacklinks = useCallback(
    (sourceBlockId) =>
      docId ? getBacklinksForBlock(index, docId, sourceBlockId) : [],
    [index, docId]
  );

  return { ready, index, backlinksByBlockId, getBacklinks };
}
